import {
    downloadGLTF,
    RenderStateDynamicInstance,
    RenderStateDynamicMesh,
    RenderStateDynamicObject,
    RGBA,
} from "@novorender/api";
import { ReadonlyQuat, ReadonlyVec3 } from "gl-matrix";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Permission } from "apis/dataV2/permissions";
import { useAppSelector } from "app/redux-store-interactions";
import { featuresConfig } from "config/features";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { areArraysEqual } from "features/arcgis/utils";
import { CameraType, selectCameraType } from "features/render";
import { useAbortController } from "hooks/useAbortController";
import { useCheckProjectPermission } from "hooks/useCheckProjectPermissions";
import { selectConfig, selectWidgets } from "slices/explorer";
import { telemetry } from "telemetry";
import { AsyncState, AsyncStatus } from "types/misc";

import { formsGlobalsActions } from "../formsGlobals";
import { useDispatchFormsGlobals, useFormsGlobals } from "../formsGlobals/hooks";
import {
    selectAlwaysShowMarkers,
    selectAssets,
    selectCurrentFormsList,
    selectFormFilters,
    selectLocationForms,
    selectSelectedFormId,
    selectTemplates,
} from "../slice";
import { FormGLtfAsset, LocationTemplate } from "../types";
import { shouldShowForm } from "../utils";
import { useFetchAssetList } from "./useFetchAssetList";
import { useFetchLocationForms } from "./useFetchLocationForms";

type RenderedForm = {
    templateId: string;
    id: string;
    marker: string;
    location: ReadonlyVec3;
    rotation?: ReadonlyQuat;
    scale?: number;
};

function areRenderedFormsEqual(a: RenderedForm, b: RenderedForm) {
    return (
        a.templateId === b.templateId &&
        a.id === b.id &&
        a.marker === b.marker &&
        a.location === b.location &&
        a.rotation === b.rotation &&
        a.scale === b.scale
    );
}

const MAX_ASSET_COUNT = 100_000;
const SELECTED_OBJECT_ID_OFFSET = MAX_ASSET_COUNT / 2;
const SEMI_TRANSPARENT_OBJECT_ID_OFFSET = MAX_ASSET_COUNT / 4;

function useTransformDraft() {
    const formsGlobals = useFormsGlobals();
    return useMemo(() => formsGlobals.transformDraft, [formsGlobals]);
}

export function useRenderLocationFormAssets() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const { t } = useTranslation();
    const dispatchFormsGlobals = useDispatchFormsGlobals();
    const transform = useTransformDraft();
    const templates = useAppSelector(selectTemplates);
    const locationForms = useAppSelector(selectLocationForms);
    const formFilters = useAppSelector(selectFormFilters);
    const assetInfoList = useAppSelector(selectAssets);
    const [abortController, abort] = useAbortController();
    const selectedTemplateId = useAppSelector(selectCurrentFormsList);
    const selectedFormId = useAppSelector(selectSelectedFormId);
    const selectedMeshCache = useRef(new WeakMap<RenderStateDynamicMesh, RenderStateDynamicMesh>());
    const semiTransparentMeshCache = useRef(new WeakMap<RenderStateDynamicMesh, RenderStateDynamicMesh>());
    const isFormsWidgetOpen = useAppSelector((state) => selectWidgets(state).includes(featuresConfig.forms.key));
    const alwaysShowMarkers = useAppSelector(selectAlwaysShowMarkers);
    const active = useAppSelector(selectCameraType) === CameraType.Pinhole && (isFormsWidgetOpen || alwaysShowMarkers);
    const assetsUrl = useAppSelector(selectConfig).assetsUrl;
    const checkPermission = useCheckProjectPermission();
    const canView = checkPermission(Permission.FormsView) ?? true;

    useFetchAssetList({ skip: !active });
    useFetchLocationForms();

    const [assetAbortController, assetAbort] = useAbortController();
    const [assetGltfMap, setAssetGltfMap] = useState<AsyncState<Map<string, readonly RenderStateDynamicObject[]>>>({
        status: AsyncStatus.Initial,
    });
    const needCleaning = useRef(false);

    const prevRenderedForms = useRef<RenderedForm[]>();
    const renderedForms = useMemo(() => {
        if (!canView || !active || templates.status !== AsyncStatus.Success) {
            return [];
        }

        const templateMap = new Map(templates.data.map((t) => [t.id, t]));

        const result = locationForms
            .filter((form) => form.location && shouldShowForm(form, formFilters))
            .map((form) => {
                const template = templateMap.get(form.templateId) as LocationTemplate | undefined;

                if (!template) {
                    return null;
                }

                const result = {
                    templateId: template.id,
                    id: form.id,
                    marker: template.marker,
                    location: form.location!,
                    rotation: form.rotation,
                    scale: form.scale,
                };

                if (transform && form.templateId === transform.templateId && form.id === transform.formId) {
                    result.location = transform.location;
                    result.rotation = transform.rotation;
                    result.scale = transform.scale;
                }
                return result;
            })
            .filter((form) => form !== null) as RenderedForm[];

        if (areArraysEqual(result, prevRenderedForms.current, areRenderedFormsEqual)) {
            return prevRenderedForms.current!;
        } else {
            prevRenderedForms.current = result;
            return result;
        }
    }, [templates, locationForms, active, transform, canView, formFilters]);
    const baseObjectIdSet = useMemo(() => {
        const baseObjectIdSet = new Set<number>();
        if (assetInfoList.status === AsyncStatus.Success) {
            for (const { baseObjectId } of assetInfoList.data) {
                baseObjectIdSet.add(baseObjectId);
                baseObjectIdSet.add(baseObjectId + SELECTED_OBJECT_ID_OFFSET);
                baseObjectIdSet.add(baseObjectId + SEMI_TRANSPARENT_OBJECT_ID_OFFSET);
            }
        }
        return baseObjectIdSet;
    }, [assetInfoList]);

    const uniqueMarkers = useMemo(() => {
        const uniqueMarkers = new Set<string>();
        for (const form of renderedForms) {
            uniqueMarkers.add(form.marker);
        }

        return uniqueMarkers;
    }, [renderedForms]);

    useEffect(() => {
        loadAssets();

        async function loadAssets() {
            if (!canView || !active || assetInfoList.status !== AsyncStatus.Success) {
                return;
            }

            setAssetGltfMap({ status: AsyncStatus.Initial });
            assetAbort();

            const result = new Map<string, readonly RenderStateDynamicObject[]>();

            try {
                await Promise.all(
                    [...uniqueMarkers].sort().map(async (name) => {
                        const assetInfo = assetInfoList.data.find((a) => a.name === name)!;
                        result.set(name, await loadAsset(assetsUrl, name, assetInfo, assetAbortController.current));
                    }),
                );
            } catch (e) {
                telemetry.trackCaughtException({
                    source: "forms.useRenderLocationFormAssets.loadAssets",
                    category: "data",
                    error: e,
                });
                setAssetGltfMap({
                    status: AsyncStatus.Error,
                    msg: t("loadingAssetsError"),
                });
            }

            setAssetGltfMap({ status: AsyncStatus.Success, data: result });
        }
    }, [canView, active, uniqueMarkers, assetInfoList, assetAbort, assetAbortController, assetsUrl, t]);

    const willUnmount = useRef(false);
    useEffect(() => {
        willUnmount.current = false;
        return () => {
            willUnmount.current = true;
        };
    }, []);

    useEffect(() => {
        return () => {
            abort();
        };
    }, [locationForms, abort]);

    useEffect(() => {
        updateDynamicObjects();

        return () => {
            if (willUnmount.current && needCleaning.current) {
                cleanup();
            }
        };

        function cleanup() {
            if (!view || assetInfoList.status !== AsyncStatus.Success || assetGltfMap.status !== AsyncStatus.Success) {
                return;
            }

            const objects = view.renderState.dynamic.objects.filter(
                (obj) => !obj.baseObjectId || !baseObjectIdSet.has(obj.baseObjectId),
            );

            needCleaning.current = false;

            view.modifyRenderState({ dynamic: { objects } });
            dispatchFormsGlobals(formsGlobalsActions.setObjectIdToFormIdMap(new Map()));
        }

        function updateDynamicObjects() {
            if (
                !canView ||
                !view ||
                assetInfoList.status !== AsyncStatus.Success ||
                assetGltfMap.status !== AsyncStatus.Success
            ) {
                return;
            }

            // Clean and exit
            if (!active) {
                if (needCleaning.current) {
                    cleanup();
                }

                return;
            }

            const assetMap = new Map<
                string,
                {
                    ref: RenderStateDynamicObject;
                    instances: RenderStateDynamicInstance[];
                    selectedInstances: RenderStateDynamicInstance[];
                    semiTransparentInstances: RenderStateDynamicInstance[];
                }[]
            >();
            assetGltfMap.data.forEach((asset, name) => {
                assetMap.set(
                    name,
                    asset.map((ref) => ({
                        ref,
                        instances: [],
                        selectedInstances: [],
                        semiTransparentInstances: [],
                    })),
                );
            });

            const objectIdToFormIdMap = new Map<number, { templateId: string; formId: string }>();

            for (const form of renderedForms) {
                const asset = assetMap.get(form.marker)!;
                if (!asset) {
                    continue;
                }

                const isSelected = selectedTemplateId === form.templateId && selectedFormId === form.id;
                const isSemiTransparent = selectedTemplateId && form.templateId !== selectedTemplateId;

                for (const { ref, instances, selectedInstances, semiTransparentInstances } of asset) {
                    for (const refInst of ref.instances) {
                        const position = form.location;
                        const rotation = form.rotation;
                        const scale = form.scale;

                        let objectId: number;
                        if (isSelected) {
                            objectId = ref.baseObjectId! + SELECTED_OBJECT_ID_OFFSET + selectedInstances.length;
                            selectedInstances.push({ ...refInst, position, rotation, scale });
                        } else if (isSemiTransparent) {
                            objectId =
                                ref.baseObjectId! + SEMI_TRANSPARENT_OBJECT_ID_OFFSET + semiTransparentInstances.length;
                            semiTransparentInstances.push({ ...refInst, position, rotation, scale });
                        } else {
                            objectId = ref.baseObjectId! + instances.length;
                            instances.push({ ...refInst, position, rotation, scale });
                        }

                        objectIdToFormIdMap.set(objectId, {
                            templateId: form.templateId,
                            formId: form.id,
                        });
                    }
                }
            }

            let objects = view.renderState.dynamic.objects as RenderStateDynamicObject[];
            if (needCleaning.current) {
                objects = view.renderState.dynamic.objects.filter(
                    (obj) => !obj.baseObjectId || !baseObjectIdSet.has(obj.baseObjectId),
                );

                needCleaning.current = false;
            } else {
                objects = objects.slice();
            }

            const addInstancesWithMesh = (
                instances: RenderStateDynamicInstance[],
                meshCache: WeakMap<RenderStateDynamicMesh, RenderStateDynamicMesh>,
                createMesh: (mesh: RenderStateDynamicMesh) => RenderStateDynamicMesh,
                objectIdOffset: number,
                refMesh: RenderStateDynamicMesh,
                baseObjectId: number,
            ) => {
                if (!instances.length) {
                    return;
                }

                let mesh = meshCache.get(refMesh);
                if (!mesh) {
                    mesh = createMesh(refMesh);
                    meshCache.set(refMesh, mesh);
                }
                objects.push({
                    mesh,
                    baseObjectId: baseObjectId + objectIdOffset,
                    instances,
                });
            };

            assetMap.forEach((assets) => {
                for (const { ref, instances, selectedInstances, semiTransparentInstances } of assets) {
                    if (instances.length) {
                        objects.push({ ...ref, instances });
                    }

                    addInstancesWithMesh(
                        selectedInstances,
                        selectedMeshCache.current,
                        createSelectedMesh,
                        SELECTED_OBJECT_ID_OFFSET,
                        ref.mesh,
                        ref.baseObjectId!,
                    );

                    addInstancesWithMesh(
                        semiTransparentInstances,
                        semiTransparentMeshCache.current,
                        createSemiTransparentMesh,
                        SEMI_TRANSPARENT_OBJECT_ID_OFFSET,
                        ref.mesh,
                        ref.baseObjectId!,
                    );
                }
            });

            needCleaning.current = true;
            view.modifyRenderState({ dynamic: { objects } });
            dispatchFormsGlobals(formsGlobalsActions.setObjectIdToFormIdMap(objectIdToFormIdMap));
        }
    }, [
        renderedForms,
        view,
        assetInfoList,
        abortController,
        selectedTemplateId,
        selectedFormId,
        active,
        assetGltfMap,
        dispatchFormsGlobals,
        canView,
        baseObjectIdSet,
    ]);
}

function createMeshWithColor(
    mesh: RenderStateDynamicMesh,
    colorTransform: (color: RGBA) => RGBA,
): RenderStateDynamicMesh {
    mesh = structuredClone(mesh);
    for (const p of mesh.primitives) {
        (p.material.baseColorFactor as RGBA) = colorTransform(p.material.baseColorFactor as RGBA);
    }
    return mesh;
}

function createSelectedMesh(mesh: RenderStateDynamicMesh): RenderStateDynamicMesh {
    return createMeshWithColor(mesh, () => [1, 0, 0, 1]);
}

function createSemiTransparentMesh(mesh: RenderStateDynamicMesh): RenderStateDynamicMesh {
    const SEMI_TRANSPARENT_OPACITY = 0.2;
    return createMeshWithColor(mesh, (color) =>
        color ? [color[0], color[1], color[2], SEMI_TRANSPARENT_OPACITY] : [1, 1, 1, SEMI_TRANSPARENT_OPACITY],
    );
}

const ASSET_CACHE = new Map<string, readonly RenderStateDynamicObject[]>();

async function loadAsset(assetsUrl: string, name: string, asset: FormGLtfAsset, abortController: AbortController) {
    let loadedGltfList = ASSET_CACHE.get(name);
    if (!loadedGltfList) {
        const url = getAssetGlbUrl(assetsUrl, asset.name);
        loadedGltfList = await downloadGLTF(new URL(url), asset.baseObjectId, abortController);
        ASSET_CACHE.set(name, loadedGltfList);
    }
    return loadedGltfList;
}

function getAssetGlbUrl(assetsUrl: string, asset: string) {
    return `${assetsUrl}/glbs/${asset}.glb`;
}
